import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Popover, Checkbox, Interactive } from '@highlight/ui'
import { Popover as BasePopover } from '@base-ui/react/popover'
import { Calendar, Check, Clock, Globe } from 'lucide-react'
import type { StagedActionEditTag, StagedActionView } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { CalendarGrid } from '@renderer/overlay/Sidepanel/components/reminders/date-picker/nested/calendar-grid'
import { TimeSuggestions } from '@renderer/overlay/Sidepanel/components/reminders/date-picker/nested/time-suggestions'
import { TimeField } from '@renderer/overlay/Sidepanel/components/reminders/date-picker/nested/time-field'
import {
  formatUserTime,
  usePreferredHourCycle,
} from '@renderer/overlay/Sidepanel/components/reminders/utils/user-time-format'
import type { PreferredHourCycle } from '@shared/ipc/routers/BaseIpc.router'
import { useEditOptions } from './edit-options-context'
import { ActionTagBadgeCollection } from './badge-collection'
import {
  parseWhenPayload,
  payloadToDraft,
  draftToPayload,
  zonedWallClockToInstant,
  instantToZonedWallClock,
  type WhenDraft,
} from './datetime-wire'
import { DayTimeline } from './day-timeline'
import { eventOverlapsDay, excludeEditedEvent, type TimelineEvent } from './day-timeline-layout'
import { tagCanonicalValues } from './tag-canonical-values'
import { reframeAttendeesToZone } from './attendee-reframe'
import { useCalendarEvents } from '@renderer/hooks/use-calendar-events'
import { useAttendeeFreeBusy } from '@renderer/hooks/use-attendee-freebusy'
import { alignFreeBusyAttendees } from '@renderer/hooks/freebusy-align'

interface EditableDateTimeProps {
  keyName: string
  tags: Array<StagedActionView['tags'][number]>
  editTag: StagedActionEditTag
}

const HOUR_MS = 60 * 60 * 1000

const weekdayDateFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
})

/** "Mon, Jun 2" — the date half of a start/end field. */
function formatFieldDate(date: Date): string {
  return weekdayDateFormatter.format(date)
}

/** "HH:MM" 24h string for a JS Date, as consumed by TimeField / TimeSuggestions. */
function getTimeString(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

/** Apply a "HH:MM" wall-clock time onto a date, keeping its calendar day. */
function applyTimeString(date: Date, time: string): Date {
  const [hours, minutes] = time.split(':').map(Number)
  const next = new Date(date)
  next.setHours(hours, minutes, 0, 0)
  return next
}

/** Apply a calendar day onto a date, keeping its wall-clock time. */
function applyDay(target: Date, day: Date): Date {
  const next = new Date(target)
  next.setFullYear(day.getFullYear(), day.getMonth(), day.getDate())
  return next
}

/**
 * Short, human timezone label (e.g. "PDT" / "GMT+1") for an IANA zone, resolved
 * at the given instant so DST is reflected. Used for the trigger label and the
 * per-zone offset hints in the timezone selector.
 */
function formatTimeZoneLabel(timeZone: string | undefined, at: Date): string | null {
  if (!timeZone) return null
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZone, timeZoneName: 'short' }).formatToParts(at)
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? null
  } catch {
    return null
  }
}

/** A compact field button (date or time) that opens its own nested popover. */
function FieldPopover({
  icon,
  label,
  disabled,
  className,
  children,
}: {
  icon: React.ReactNode
  label: string
  disabled?: boolean
  /** Extra classes merged onto the trigger (e.g. flex weighting). */
  className?: string
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  return (
    <BasePopover.Root open={open} onOpenChange={(v) => !disabled && setOpen(v)}>
      <BasePopover.Trigger
        disabled={disabled}
        className={twMerge(
          'border-border hover:bg-foreground/[0.03] text-foreground-secondary data-[popup-open]:border-info flex h-7 min-w-0 flex-1 items-center gap-1.5 rounded-lg border bg-transparent px-2 text-[12px] font-medium outline-none transition-colors disabled:opacity-50',
          className,
        )}
      >
        <span className="text-foreground-placeholder shrink-0">{icon}</span>
        <span className="truncate">{label}</span>
      </BasePopover.Trigger>
      <Popover.Content sideOffset={4} className="flex w-60 flex-col rounded-xl p-2 dark:outline-0">
        {/* Wrap the portaled popup so the overlay registers it as click-catching;
            otherwise it lands in the transparent click-through region and pointer
            events fall through to the desktop. */}
        <Interactive>{children}</Interactive>
      </Popover.Content>
    </BasePopover.Root>
  )
}

/** Date trigger → month grid. Picks a calendar day, preserving the time. */
function DateFieldButton({
  date,
  disabled,
  className,
  onPickDay,
}: {
  date: Date
  disabled?: boolean
  className?: string
  onPickDay: (day: Date) => void
}) {
  return (
    <FieldPopover
      icon={<Calendar className="size-3.5" />}
      label={formatFieldDate(date)}
      disabled={disabled}
      className={className}
    >
      <CalendarGrid selected={date} onSelect={(day) => day && onPickDay(day)} />
    </FieldPopover>
  )
}

/** Time trigger → preset list + free-form time entry. Picks a wall-clock time. */
function TimeFieldButton({
  date,
  hourCycle,
  disabled,
  className,
  onPickTime,
}: {
  date: Date
  hourCycle: PreferredHourCycle | null
  disabled?: boolean
  className?: string
  onPickTime: (time: string) => void
}) {
  const timeString = getTimeString(date)
  return (
    <FieldPopover
      icon={<Clock className="size-3.5" />}
      label={formatUserTime(date, hourCycle)}
      disabled={disabled}
      className={className}
    >
      <div className="mb-2 flex items-center gap-2 border-b px-1 pb-2">
        <Clock className="text-foreground-placeholder size-3.5 shrink-0" />
        <TimeField value={timeString} onFocus={() => {}} onChange={onPickTime} />
      </div>
      <TimeSuggestions selectedTime={timeString} onSelect={onPickTime} />
    </FieldPopover>
  )
}

/** All IANA zones the runtime knows, or [] on engines without supportedValuesOf. */
function listTimeZones(): Array<string> {
  const fn = (Intl as { supportedValuesOf?: (key: 'timeZone') => Array<string> }).supportedValuesOf
  return typeof fn === 'function' ? fn('timeZone') : []
}

/**
 * Timezone trigger → searchable IANA zone list. Picking a zone re-anchors the
 * event to it while keeping the wall-clock the user sees (the instant shifts).
 */
function TimeZoneFieldButton({
  timeZone,
  at,
  disabled,
  onPickTimeZone,
}: {
  timeZone: string | undefined
  at: Date
  disabled?: boolean
  onPickTimeZone: (zone: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const zones = useMemo(() => listTimeZones(), [])
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const matches = q ? zones.filter((z) => z.toLowerCase().includes(q)) : zones
    return matches.slice(0, 200)
  }, [zones, query])
  const label = (timeZone && formatTimeZoneLabel(timeZone, at)) || timeZone || 'Local time'

  return (
    <BasePopover.Root open={open} onOpenChange={(v) => !disabled && setOpen(v)}>
      <BasePopover.Trigger
        disabled={disabled}
        className="text-foreground-placeholder hover:text-foreground-secondary hover:bg-foreground/[0.04] data-[popup-open]:text-foreground-secondary -ml-1 flex items-center gap-1 rounded-md px-1 py-0.5 text-[11px] font-medium outline-none transition-colors disabled:opacity-50"
      >
        <Globe className="size-3 shrink-0 opacity-70" />
        <span className="truncate">{label}</span>
      </BasePopover.Trigger>
      <Popover.Content sideOffset={4} className="flex w-64 flex-col rounded-xl p-2 dark:outline-0">
        <Interactive>
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search timezone…"
            className="text-foreground-secondary placeholder:text-foreground-disabled focus:border-info mb-2 h-7 shrink-0 rounded-lg border bg-transparent px-2 text-[12px] font-medium outline-none transition-colors"
          />
          <div className="flex max-h-56 flex-col gap-0.5 overflow-y-auto overscroll-contain">
            {filtered.length === 0 ? (
              <div className="text-foreground-placeholder px-3 py-2 text-[12px]">No matches</div>
            ) : (
              filtered.map((zone) => {
                const selected = zone === timeZone
                return (
                  <button
                    key={zone}
                    type="button"
                    onClick={() => {
                      onPickTimeZone(zone)
                      setOpen(false)
                    }}
                    className={`flex items-center justify-between gap-2 rounded-lg px-3 py-1.5 text-left text-[12px] transition-colors ${
                      selected ? 'bg-info-subtle text-info-subtle' : 'hover:bg-element-hover text-foreground-secondary'
                    }`}
                  >
                    <span className="truncate">{zone.replace(/_/g, ' ')}</span>
                    <span className="text-foreground-placeholder shrink-0 text-[11px]">
                      {formatTimeZoneLabel(zone, at) ?? ''}
                    </span>
                  </button>
                )
              })
            )}
          </div>
        </Interactive>
      </Popover.Content>
    </BasePopover.Root>
  )
}

/**
 * Start + end date/time editor for calendar events, anchored on the `Date`
 * view tag. Seeds from the RAW current when carried in `editTag.options[0].value`
 * (NOT the formatted Date/Time display badges) and serializes back to the exact
 * backend wire-format on every change.
 *
 * Exposes four independently-editable controls — Start date, Start time, End
 * date, End time — each its own small popover (a month `CalendarGrid` for dates,
 * a preset list + free-form `TimeField` for times). An all-day toggle hides the
 * time fields and edits dates only. A single compact row of event-level duration
 * presets (+30m / +1h) nudges the end relative to the start; there are no
 * confusing per-field "Tonight / This weekend" presets.
 *
 * The event's IANA `timeZone` is editable via a small selector; changing it keeps
 * the wall-clock the user sees and re-anchors the instant (see datetime-wire.ts).
 * The draft Dates are naive wall-clock in that zone.
 *
 * The seed comes from the staged action's inputData, which for an update may be
 * stale relative to the live event — consistent with the other editable fields.
 * We don't live-fetch.
 */
export function EditableDateTime({ keyName, tags, editTag }: EditableDateTimeProps) {
  const { onUpdate, isUpdating, editOptions, viewTags, toolSet } = useEditOptions()
  const [open, setOpen] = useState(false)
  const hourCycle = usePreferredHourCycle()

  const seedDraft = useMemo<WhenDraft | null>(() => {
    const payload = parseWhenPayload(editTag.options[0]?.value)
    return payload ? payloadToDraft(payload) : null
  }, [editTag.options])

  // The event's ORIGINAL when, captured once. After a save we invalidate the
  // edit-options query, so `seedDraft` re-seeds to the committed value — but the
  // synced calendar row keeps the original time until its next poll. Keeping the
  // stable original lets us also exclude that still-original row from the timeline
  // (no phantom self-conflict) until it re-syncs.
  const originalSeedRef = useRef<WhenDraft | null>(seedDraft)
  if (originalSeedRef.current === null && seedDraft !== null) {
    originalSeedRef.current = seedDraft
  }

  // Local editing copy of the draft. Optimistic — we keep showing the user's
  // edits while a save is in flight, and re-seed only when the popover reopens.
  const [draft, setDraft] = useState<WhenDraft | null>(seedDraft)
  // The last server-CONFIRMED value: the initial seed, then each successful save,
  // then any fresh seed that lands while the popover is closed. A failed save
  // reverts to THIS, not to `seedDraft` — which stays stale right after a prior
  // successful save (until the edit-options refetch settles) and would otherwise
  // restore a time OLDER than the server actually holds.
  const lastSavedRef = useRef<WhenDraft | null>(seedDraft)
  const lastSeedRef = useRef<string | undefined>(editTag.options[0]?.value)
  const currentSeed = editTag.options[0]?.value
  if (!open && currentSeed !== lastSeedRef.current) {
    lastSeedRef.current = currentSeed
    lastSavedRef.current = seedDraft
    setDraft(seedDraft)
  }

  // Serialize date/time saves ourselves: the mutation layer doesn't, and
  // `isUpdating` (React Query's async `isPending`) is stale within a render tick,
  // so it can't gate overlapping PATCHes reliably on its own. Track in-flight
  // SYNCHRONOUSLY and coalesce — queue only the LATEST pending draft, then flush
  // it once the current save settles. Net: never two PATCHes at once, never drop
  // the user's most recent edit, and the visible draft always reflects intent.
  const inFlightRef = useRef(false)
  const pendingRef = useRef<WhenDraft | null>(null)
  // The latest committed draft, so a FAILED save only reverts the visible draft
  // when it's still the user's most recent intent (not superseded by a newer edit).
  const latestDraftRef = useRef<WhenDraft | null>(null)

  const sendUpdate = useCallback(
    (next: WhenDraft) => {
      inFlightRef.current = true
      // On SUCCESS, record this as the last server-confirmed value. On FAILURE,
      // useUpdateStagedActionView rolls the caches back to the server value; revert
      // our optimistic draft to match — to the last CONFIRMED value (not the seed,
      // which can be stale right after a prior save), and only while this is still
      // the user's most recent intent.
      Promise.resolve(onUpdate({ tagEdits: { [keyName]: [JSON.stringify(draftToPayload(next))] } })).then(
        () => {
          lastSavedRef.current = next
        },
        () => {
          if (latestDraftRef.current === next) setDraft(lastSavedRef.current ?? seedDraft)
        },
      )
    },
    [keyName, onUpdate, seedDraft],
  )

  const commit = useCallback(
    (next: WhenDraft) => {
      latestDraftRef.current = next
      setDraft(next) // keep the visible draft truthful to the user's latest intent
      // Queue (coalescing to the latest) while our own save or any other update on
      // this action is in flight; the effect below flushes it on settle.
      if (inFlightRef.current || isUpdating) {
        pendingRef.current = next
        return
      }
      sendUpdate(next)
    },
    [isUpdating, sendUpdate],
  )

  // When the in-flight save settles (isUpdating: true → false), clear the flag and
  // flush the latest queued draft, if any.
  useEffect(() => {
    if (isUpdating) return
    inFlightRef.current = false
    if (pendingRef.current) {
      const next = pendingRef.current
      pendingRef.current = null
      sendUpdate(next)
    }
  }, [isUpdating, sendUpdate])

  // Moving the START preserves the duration (timed) or shifts the end if needed
  // (all-day). For timed events we guard a degenerate (non-positive) duration with
  // +1h — matching the wire serializer, which expands end <= start to +1h — so the
  // visible draft never disagrees with the PATCHed payload or shows a 0/inverted
  // range mid-edit.
  const moveStart = useCallback(
    (nextStart: Date) => {
      if (!draft) return
      const duration = draft.endDate.getTime() - draft.startDate.getTime()
      const nextEnd = draft.allDay
        ? nextStart > draft.endDate
          ? nextStart
          : draft.endDate
        : new Date(nextStart.getTime() + (duration > 0 ? duration : HOUR_MS))
      commit({ ...draft, startDate: nextStart, endDate: nextEnd })
    },
    [draft, commit],
  )

  // Editing the END clamps it to never precede the start. Timed events require a
  // strictly-positive duration (an end <= start expands to +1h, matching the wire
  // serializer) so the draft never shows a 0-duration range that the saved payload
  // silently lengthens; all-day allows an equal end (a single inclusive day).
  const moveEnd = useCallback(
    (nextEnd: Date) => {
      if (!draft) return
      const clampedEnd = draft.allDay
        ? nextEnd < draft.startDate
          ? draft.startDate
          : nextEnd
        : nextEnd <= draft.startDate
          ? new Date(draft.startDate.getTime() + HOUR_MS)
          : nextEnd
      commit({ ...draft, endDate: clampedEnd })
    },
    [draft, commit],
  )

  // Resizing the START edge (drag the block's top): move the start but KEEP the
  // end, clamping so the start stays at least 15 min before the end. Unlike
  // `moveStart` (which preserves duration), this changes the duration.
  const setStartOnly = useCallback(
    (nextStart: Date) => {
      if (!draft) return
      const maxStart = new Date(draft.endDate.getTime() - 15 * 60 * 1000)
      commit({ ...draft, startDate: nextStart > maxStart ? maxStart : nextStart })
    },
    [draft, commit],
  )

  const handleAllDayToggle = useCallback(
    (checked: boolean) => {
      if (!draft) return
      if (checked) {
        // Timed → all-day: drop the time, keep the calendar days.
        const start = new Date(draft.startDate)
        start.setHours(0, 0, 0, 0)
        const end = new Date(draft.endDate)
        end.setHours(0, 0, 0, 0)
        commit({ ...draft, allDay: true, startDate: start, endDate: end < start ? start : end })
      } else {
        // All-day → timed: default to 9:00–10:00 local on the same days.
        const start = new Date(draft.startDate)
        start.setHours(9, 0, 0, 0)
        const end = new Date(draft.endDate)
        end.setHours(10, 0, 0, 0)
        commit({
          ...draft,
          allDay: false,
          startDate: start,
          endDate: end <= start ? new Date(start.getTime() + HOUR_MS) : end,
        })
      }
    },
    [draft, commit],
  )

  // Event-level duration nudge: keep the start, set the end to start + minutes.
  const setDuration = useCallback(
    (minutes: number) => {
      if (!draft) return
      commit({ ...draft, endDate: new Date(draft.startDate.getTime() + minutes * 60 * 1000) })
    },
    [draft, commit],
  )

  // Changing the timezone keeps the wall-clock the user sees and re-anchors the
  // event to the new zone (the absolute instant shifts) — see datetime-wire.ts.
  const moveTimeZone = useCallback(
    (timeZone: string) => {
      if (!draft) return
      commit({ ...draft, timeZone })
    },
    [draft, commit],
  )

  // The user's OTHER events, sourced from the locally-synced calendar window
  // (GlobalState `calendar.todaysEvents`, already aggregated/filtered). No new
  // network call — if the edited day falls outside that window the array simply
  // won't include it and the timeline renders its empty state (out-of-window
  // backfetch is a deferred fast-follow). Filtered to the edited day in local tz.
  const { data: allCalendarEvents } = useCalendarEvents()
  const dayEvents = useMemo<Array<TimelineEvent>>(() => {
    if (!draft) return []
    // Exclude the event under edit so it never shows as an "other" event or
    // self-conflicts. Match by TRUE instant (each seed's wall-clock resolved in its
    // own timeZone) so the synced row is recognised even for a non-local zone.
    // Exclude BOTH the ORIGINAL seed and the CURRENT (committed) seed: after a save
    // we re-seed to the committed value, but the synced calendar row keeps the
    // original time until its next poll, so the row must be matched at either time.
    // We do NOT exclude the live draft — that would hide a real conflict at the
    // proposed slot. A brand-new create has no synced row, so nothing is removed.
    const identityOf = (d: WhenDraft) => ({
      startMs: zonedWallClockToInstant(d.startDate, d.timeZone).getTime(),
      endMs: zonedWallClockToInstant(d.endDate, d.timeZone).getTime(),
    })
    // Candidate identities in PRIORITY order: the ORIGINAL seed (the synced row's
    // time until the calendar re-polls) then the CURRENT committed seed (where the
    // row lands after a save). excludeEditedEvent removes AT MOST ONE row, so an
    // unrelated meeting at the same slot still counts toward conflicts and is never
    // dropped alongside the edited event.
    const editedIdentities = [originalSeedRef.current, seedDraft]
      .filter((d): d is WhenDraft => d != null)
      .map(identityOf)
    const withoutEdited = excludeEditedEvent(allCalendarEvents, editedIdentities)
    // The picker edits in the event's own timezone (the draft Dates are naive
    // wall-clock in that zone) while synced events carry true instants. When the
    // event is anchored to a NON-local zone, re-project the other events into that
    // same wall-clock frame so the timeline band, conflicts, and day all line up.
    // Skipped entirely for local-zone events (the common case), so it's a no-op there.
    const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const reframeZone = draft.timeZone && draft.timeZone !== localZone ? draft.timeZone : undefined
    const projected = reframeZone
      ? withoutEdited.map((e) => ({
          ...e,
          start: instantToZonedWallClock(e.start, reframeZone),
          end: e.end ? instantToZonedWallClock(e.end, reframeZone) : e.end,
        }))
      : withoutEdited
    // Keep events that INTERSECT the edited day (in that same frame), not just
    // those with an endpoint on it: a timed event spanning midnight-to-midnight
    // still overlaps and must reach DayTimeline, which clamps it to the day.
    return projected.filter((e) => eventOverlapsDay(e, draft.startDate))
  }, [allCalendarEvents, draft, seedDraft])

  // Attendee FREE/BUSY ("Phase 2"). We need each guest's EMAIL to query
  // free/busy, but the `Guest` VIEW tags carry the resolved contact NAME, not
  // the email (see the calendar toolset's `buildAttendeeTag`); the canonical
  // emails live on the `Guest` edit-tag `options` (`value` = email). Reconcile
  // the LIVE guest view tags with those options (`tagCanonicalValues`) so the
  // list reflects adds/removes immediately while still resolving each guest to
  // its email — reading either source alone either drops named attendees
  // (view-tags-only) or lags behind a recent edit (options-only). No attendees →
  // no section (the hook is disabled, DayTimeline renders nothing for [] ).
  // Guest {label (display name), value (canonical email)} pairs, reconciled from
  // the live Guest view tags + edit options. Drives both the free/busy lookup
  // (emails) and the availability rows (names).
  const guests = useMemo(() => {
    const guestViewTags = viewTags.filter((t) => t.key === 'Guest')
    const guestOptions = editOptions?.tags?.find((t) => t.key === 'Guest')?.options ?? []
    return tagCanonicalValues(guestViewTags, guestOptions).filter((p) => p.value.includes('@'))
  }, [viewTags, editOptions])

  const attendeeEmails = useMemo<Array<string>>(() => guests.map((g) => g.value), [guests])

  // email (lowercased) → resolved display name, for the per-attendee rows.
  const guestNames = useMemo(() => new Map(guests.map((g) => [g.value.toLowerCase(), g.label])), [guests])

  const selectedCalendarId = useMemo<string | undefined>(() => {
    const calendarTag = editOptions?.tags?.find((t) => t.key === 'Calendar')
    // The Calendar selection lives in a SIBLING tag-group, so it's NOT in this
    // editor's `tags` prop (that's only the When group). Read it from the full
    // action view tags. A select view tag carries the option's display NAME
    // (see EditableSingleSelect), so we match on name OR value. Fall back to the
    // first option (the 'primary' sentinel) when there's no explicit selection.
    const viewSelection = viewTags.find((t) => t.key === 'Calendar')?.value
    if (viewSelection) {
      const match = calendarTag?.options?.find((o) => o.name === viewSelection || o.value === viewSelection)
      // A Calendar is explicitly selected. If it resolves to an option, use that
      // option's value. If it does NOT (e.g. a renamed/removed calendar no longer
      // in the options), pass the raw selection through rather than silently
      // falling back to the first option ('primary'): `useAttendeeFreeBusy`'s
      // connection resolver maps an unrecognized id to no connection → the
      // overlay shows "Availability unknown" instead of querying the wrong
      // (primary) calendar and reporting misleading free/busy.
      return match ? match.value : viewSelection
    }
    // No explicit Calendar selection → the event targets the primary calendar.
    return calendarTag?.options?.[0]?.value
  }, [editOptions, viewTags])

  // Map the action's executing toolset to its calendar provider so free/busy
  // resolves the 'primary' connection under the RIGHT account: a google_calendar
  // draft must query under a Google connection, not the user's global-primary
  // calendar (which may be a personal Microsoft account that can't see the Google
  // guests). We key off `toolSet` (the executor) NOT `sourceIntegration`, which
  // is the source-context provenance and is typically empty on calendar actions.
  const calendarProvider: 'google' | 'microsoft' | undefined =
    toolSet === 'google_calendar' ? 'google' : toolSet === 'outlook_calendar' ? 'microsoft' : undefined

  const {
    data: freeBusy,
    isLoading: freeBusyLoading,
    error: freeBusyError,
  } = useAttendeeFreeBusy({
    emails: attendeeEmails,
    calendarId: selectedCalendarId,
    start: draft?.startDate ?? new Date(),
    end: draft?.endDate ?? new Date(),
    // The draft's start/end are wall-clock in the event's zone; pass it so the
    // fetch window anchors to the same event-zone day the bands reframe to.
    // Zone-agnostic for all-day drafts (date-based; matches the reframe above).
    timeZone: draft && !draft.allDay ? draft.timeZone : undefined,
    provider: calendarProvider,
    // Only fetch while the popover is open and there are guests to look up.
    enabled: open && attendeeEmails.length > 0 && !!draft,
  })

  // A failed fetch degrades gracefully: every guest becomes `unknown` (no bands,
  // no "everyone free / N conflicts" claim) rather than vanishing — the same
  // aligned-unknown shape the no-connection path returns, so the availability
  // footer stays put ("Availability unknown") instead of disappearing whenever an
  // HTTP/network lookup fails. The Phase-1 own-events overlay is untouched.
  // Reframe the provider's true-instant busy intervals into the draft's wall-clock
  // frame — the SAME reframe applied to `dayEvents` above — so attendee conflicts
  // and bands line up with the proposed band for events anchored to a non-local
  // timezone. No-op for local-zone events (the common case).
  const attendeeFreeBusy = useMemo(() => {
    // Prefer the last good result (React Query retains `freeBusy` across a failed
    // REFETCH), so a transient refetch error doesn't throw away real busy/free
    // data — or a `reconnectRequired` we already learned. Only when there is NO
    // prior data AND the (first) fetch errored do we synthesize aligned `unknown`
    // rows, so the footer still shows instead of vanishing.
    const raw = freeBusy?.attendees ?? (freeBusyError ? alignFreeBusyAttendees(attendeeEmails, []) : [])
    const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    // All-day drafts are date-based and zone-agnostic (the wire omits timeZone and
    // eventAvailabilitySpan uses local midnights), so ignore any zone left over
    // from a timed→all-day switch — otherwise the reframe would skew busy
    // intervals against the local-day availability span.
    const reframeZone =
      draft && !draft.allDay && draft.timeZone && draft.timeZone !== localZone ? draft.timeZone : undefined
    return reframeAttendeesToZone(raw, reframeZone)
  }, [freeBusy, freeBusyError, attendeeEmails, draft?.timeZone, draft?.allDay])

  // No raw seed (event has no editable time) — fall back to a plain badge.
  if (!draft) {
    return <ActionTagBadgeCollection keyName={keyName} tags={tags} />
  }

  return (
    <Popover open={open} onOpenChange={(v) => !isUpdating && setOpen(v)}>
      <BasePopover.Trigger
        render={<div />}
        className={`flex flex-wrap gap-2 ${isUpdating ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
      >
        <ActionTagBadgeCollection keyName={keyName} tags={tags} editable />
      </BasePopover.Trigger>
      <Popover.Content
        sideOffset={4}
        disableAnchorTracking
        className="flex w-[480px] gap-3 rounded-xl p-3 dark:outline-0"
      >
        {/* Wrap the portaled popup so the overlay registers its full 480px width as
            click-catching. It extends left of the sidepanel into the transparent
            click-through region, so without this, pointer events (including the
            timeline drag) fall through to the desktop instead of the popup. */}
        <Interactive>
          {/* Left column: day timeline overlay of the user's other events plus
              per-attendee free/busy bands and a known-only availability summary. */}
          <DayTimeline
            day={draft.startDate}
            start={draft.startDate}
            end={draft.endDate}
            allDay={draft.allDay}
            events={dayEvents}
            hourCycle={hourCycle}
            disabled={isUpdating}
            onMove={moveStart}
            onResizeStart={setStartOnly}
            onResizeEnd={moveEnd}
            attendees={attendeeFreeBusy}
            guestNames={guestNames}
            freeBusyLoading={freeBusyLoading}
            // Honor a retained `reconnectRequired` even across a failed refetch
            // (React Query keeps the last good `freeBusy`); a first-fetch error
            // leaves `freeBusy` undefined → false anyway.
            reconnectRequired={!!freeBusy?.reconnectRequired}
          />

          {/* Right column: the four date/time fields. */}
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            {/* All-day toggle */}
            <label
              className={`text-foreground-secondary hover:text-foreground flex items-center gap-2 text-xs font-medium ${
                isUpdating ? 'pointer-events-none' : 'cursor-pointer'
              }`}
            >
              <Checkbox
                checked={draft.allDay}
                onCheckedChange={handleAllDayToggle}
                disabled={isUpdating}
                className="size-4"
              >
                <Checkbox.Indicator>
                  <Check className="size-3 text-white" />
                </Checkbox.Indicator>
              </Checkbox>
              All day
            </label>

            <div className="border-t" />

            {/* Starts */}
            <div className="flex items-center gap-2">
              <span className="text-foreground-placeholder w-12 shrink-0 text-xs font-medium">Starts</span>
              <DateFieldButton
                date={draft.startDate}
                disabled={isUpdating}
                className="flex-[1.6]"
                onPickDay={(day) => moveStart(applyDay(draft.startDate, day))}
              />
              {!draft.allDay && (
                <TimeFieldButton
                  date={draft.startDate}
                  hourCycle={hourCycle}
                  disabled={isUpdating}
                  onPickTime={(time) => moveStart(applyTimeString(draft.startDate, time))}
                />
              )}
            </div>

            {/* Ends */}
            <div className="flex items-center gap-2">
              <span className="text-foreground-placeholder w-12 shrink-0 text-xs font-medium">Ends</span>
              <DateFieldButton
                date={draft.endDate}
                disabled={isUpdating}
                className="flex-[1.6]"
                onPickDay={(day) => moveEnd(applyDay(draft.endDate, day))}
              />
              {!draft.allDay && (
                <TimeFieldButton
                  date={draft.endDate}
                  hourCycle={hourCycle}
                  disabled={isUpdating}
                  onPickTime={(time) => moveEnd(applyTimeString(draft.endDate, time))}
                />
              )}
            </div>

            {/* Timezone label + duration presets (timed only) */}
            {!draft.allDay && (
              <div className="flex items-center justify-between pt-0.5">
                <TimeZoneFieldButton
                  timeZone={draft.timeZone}
                  at={draft.startDate}
                  disabled={isUpdating}
                  onPickTimeZone={moveTimeZone}
                />
                <div className="flex items-center gap-1">
                  {[
                    { label: '30m', minutes: 30 },
                    { label: '1h', minutes: 60 },
                  ].map(({ label, minutes }) => (
                    <button
                      key={label}
                      type="button"
                      disabled={isUpdating}
                      onClick={() => setDuration(minutes)}
                      className="hover:bg-foreground/[0.03] text-foreground-secondary hover:text-foreground rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-50"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Interactive>
      </Popover.Content>
    </Popover>
  )
}
